import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, InputAdornment } from '@mui/material';
import { useTranslation } from 'react-i18next';
import BaseInput from 'components/inputs/base-input';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import CheckIcon from '@mui/icons-material/Check';
import api from 'utils/api';
import { colors } from 'styles/colors';
import { AnalyticsFilters } from './FugaAnalytics';

interface TrackOption {
  trackId: string;
  trackName: string;
}

interface TrackSearchInputProps {
  spProfile: string | null;
  // Toggle a track on/off the chart (add if not selected, remove if already selected).
  onToggle: (trackId: string) => void;
  // Track IDs currently on the chart — shown with a green check in the dropdown.
  selectedTrackIds?: string[];
  // Active chart filters — the catalog is restricted to tracks that have streams
  // matching these (backend applies them; 'all'/'world' = no filter).
  filters?: AnalyticsFilters;
}

// Let the user toggle the artist's tracks on/off the "Streams by Track" comparison.
// The catalog is fetched per artist + active filters, then searched client-side.
export default function TrackSearchInput({
  spProfile,
  onToggle,
  selectedTrackIds = [],
  filters,
}: TrackSearchInputProps) {
  const { t } = useTranslation();
  const [allTracks, setAllTracks] = useState<TrackOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // Refetch when the artist or any filter changes; the API returns only tracks that
  // have streams under those filters (alpha-ordered). Typing then filters locally.
  useEffect(() => {
    let isMounted = true;
    if (!spProfile) {
      setAllTracks([]);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const results: TrackOption[] = await api.analytics.searchTracks({
          params: { spProfile, ...filters },
        });
        if (isMounted) setAllTracks(results || []);
      } catch (error) {
        console.log(error);
        if (isMounted) setAllTracks([]);
      }
      if (isMounted) setLoading(false);
    })();
    return () => {
      isMounted = false;
    };
  }, [spProfile, filters?.dsp, filters?.dateRange, filters?.country]);

  return (
    <Autocomplete
      fullWidth
      size="small"
      disabled={!spProfile}
      loading={loading}
      options={allTracks}
      disableCloseOnSelect
      value={null}
      inputValue={inputValue}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.trackName)}
      isOptionEqualToValue={(option, value) => option.trackId === value.trackId}
      noOptionsText={t('No tracks found')}
      loadingText={t('Loading…')}
      onInputChange={(_event, value, reason) => {
        // Keep the search text when a click toggles a track (ignore MUI's 'reset').
        if (reason === 'input') setInputValue(value);
        else if (reason === 'clear') setInputValue('');
      }}
      onChange={(_event, option) => {
        if (option && typeof option !== 'string') onToggle(option.trackId);
      }}
      renderOption={(props, option) => {
        // Track titles can repeat within an artist; key on the unique spTrackId.
        const selected = selectedTrackIds.includes(option.trackId);
        return (
          <Box
            component="li"
            {...props}
            key={option.trackId}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Box
              component="span"
              sx={{
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {option.trackName}
            </Box>
            {selected && (
              <CheckIcon sx={{ color: colors.system.success, fontSize: 18, flexShrink: 0 }} />
            )}
          </Box>
        );
      }}
      renderInput={(params) => (
        <BaseInput
          {...params}
          name="trackSearch"
          variant="filled"
          hiddenLabel
          placeholder={t('Add tracks to compare')}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? (
                  <Box sx={{ display: 'flex', mr: 1 }}>
                    <CircularProgress size={18} sx={{ color: '#333' }} />
                  </Box>
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
